import { useEffect } from "react";

/**
 * Lightweight SEO helper — sets document.title, meta description, OG, canonical,
 * and injects optional JSON-LD schema(s).
 *
 * Usage:
 *   <SEO title="..." description="..." canonical="https://.../page" schemas={[ {...} ]} />
 */
export default function SEO({
  title,
  description,
  canonical,
  image,
  type = "website",
  schemas = [],
}) {
  const schemasKey = JSON.stringify(schemas);
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (key, kind, content) => {
      // kind: "name" | "property"
      // key:  e.g. "description", "og:title", "twitter:card"
      if (!content) return;
      const selector = `meta[${kind}="${key}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(kind, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", "name", description);
    setMeta("og:title", "property", title);
    setMeta("og:description", "property", description);
    setMeta("og:type", "property", type);
    setMeta("og:image", "property", image);
    setMeta("og:url", "property", canonical);
    setMeta("twitter:card", "name", "summary_large_image");
    setMeta("twitter:title", "name", title);
    setMeta("twitter:description", "name", description);
    setMeta("twitter:image", "name", image);

    // canonical
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    if (canonical) link.setAttribute("href", canonical);

    // schemas
    document
      .querySelectorAll('script[data-si-schema="true"]')
      .forEach((s) => s.remove());
    schemas.forEach((s) => {
      const tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.setAttribute("data-si-schema", "true");
      tag.text = JSON.stringify(s);
      document.head.appendChild(tag);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, type, schemasKey]);

  return null;
}

export function localBusinessSchema(settings, city) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Smart Infovision${city ? ` — ${city}` : ""}`,
    image: "https://installation.smartinfovision.com/logo.png",
    telephone: settings?.phone || "",
    email: settings?.email || "",
    address: {
      "@type": "PostalAddress",
      addressLocality: city || "Patna",
      addressCountry: "IN",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "421",
    },
  };
}

export function faqSchema(faq = []) {
  if (!faq.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function articleSchema(blog) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: blog.title,
    image: blog.cover_image,
    datePublished: blog.created_at,
    dateModified: blog.updated_at,
    author: { "@type": "Organization", name: blog.author || "Smart Infovision" },
    publisher: {
      "@type": "Organization",
      name: "Smart Infovision",
      logo: {
        "@type": "ImageObject",
        url: "https://installation.smartinfovision.com/logo.png",
      },
    },
    description: blog.meta_description || blog.excerpt,
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.label,
      item: it.url,
    })),
  };
}
